import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Typography,
} from "@mui/material";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";

import AppButton from "../../ui/AppButton";
import { getToken } from "../../config/msalConfig";


const EVENT_META = {
  start: { icon: InfoOutlinedIcon, color: "#3b82f6" },
  info: { icon: InfoOutlinedIcon, color: "#64748b" },
  matched: { icon: LinkRoundedIcon, color: "#0e7490" },
  unmatched: { icon: SearchOffRoundedIcon, color: "#c2410c" },
  ambiguous: { icon: SearchOffRoundedIcon, color: "#b45309" },
  done: { icon: CheckCircleOutlineIcon, color: "#16a34a" },
  error: { icon: ErrorOutlineIcon, color: "#dc2626" },
};

function LogLine({ event }) {
  const meta = EVENT_META[event.type] || EVENT_META.info;
  const Icon = meta.icon;

  return (
    <Box display="flex" alignItems="flex-start" gap={1} py={0.35}>
      <Icon sx={{ fontSize: 15, color: meta.color, flexShrink: 0, mt: "2px" }} />
      <Typography fontSize={12.5} sx={{ color: event.type === "error" ? "#dc2626" : "text.primary", lineHeight: 1.5 }}>
        {event.message || ""}
      </Typography>
    </Box>
  );
}

function EmployeeNameMappingSyncDialog({ open, onClose, filePath, onDone }) {
  const [lines, setLines] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [doneEvent, setDoneEvent] = useState(null);
  const logEndRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [lines]);

  useEffect(() => {
    if (!open) return;

    setLines([]);
    setIsDone(false);
    setDoneEvent(null);
    setIsRunning(true);

    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        const token = await getToken();
        const endpoint = `${process.env.REACT_APP_BACKEND_URI}/api/reports/employee-name-mappings/sync/stream?file_path=${encodeURIComponent(filePath)}`;

        const response = await fetch(endpoint, {
          signal: controller.signal,
          headers: { "oidc-token": token },
        });

        if (!response.ok) {
          setLines((prev) => [...prev, { type: "error", message: `Server error: ${response.status}` }]);
          setIsRunning(false);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split("\n\n");
          buffer = chunks.pop() ?? "";

          for (const chunk of chunks) {
            for (const line of chunk.split("\n")) {
              if (!line.startsWith("data: ")) continue;

              try {
                const event = JSON.parse(line.slice(6));
                if (event.type === "done") {
                  setDoneEvent(event);
                  setIsDone(true);
                  onDone?.(event);
                } else {
                  setLines((prev) => [...prev, event]);
                }
              } catch {
                // Ignore malformed event chunks.
              }
            }
          }
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          setLines((prev) => [...prev, { type: "error", message: error.message || "Connection lost" }]);
        }
      } finally {
        setIsRunning(false);
      }
    })();

    return () => controller.abort();
  }, [open, filePath]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    abortRef.current?.abort();
    onClose();
  };

  return (
    <Dialog open={open} onClose={isDone ? handleClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Sync Employee Name Mapping</DialogTitle>

      {isRunning && <LinearProgress />}

      <DialogContent sx={{ pt: 1.5 }}>
        <Typography fontSize={12} color="text.secondary" mb={1.2}>{filePath}</Typography>

        <Box
          sx={{
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 1.5,
            minHeight: 190,
            maxHeight: 340,
            overflowY: "auto",
          }}
        >
          {lines.length === 0 && isRunning && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={14} />
              <Typography fontSize={13} color="text.secondary">Starting...</Typography>
            </Box>
          )}

          {lines.map((event, index) => (
            <LogLine key={index} event={event} />
          ))}

          <div ref={logEndRef} />
        </Box>

        {isDone && doneEvent && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography fontSize={13} fontWeight={700} mb={1}>Sync complete</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip label={`${doneEvent.total || 0} total`} size="small" sx={{ bgcolor: "#f1f5f9", color: "#334155", fontWeight: 600 }} />
              <Chip label={`${doneEvent.matched || 0} matched`} size="small" sx={{ bgcolor: "#ecfeff", color: "#0e7490", fontWeight: 600 }} />
              <Chip label={`${doneEvent.unmatched || 0} unmatched`} size="small" sx={{ bgcolor: "#fff7ed", color: "#c2410c", fontWeight: 600 }} />
              <Chip label={`${doneEvent.ambiguous || 0} ambiguous`} size="small" sx={{ bgcolor: "#fffbeb", color: "#b45309", fontWeight: 600 }} />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <AppButton variant="contained" onClick={handleClose} disabled={isRunning && !isDone}>
          {isDone ? "Close" : "Cancel"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

export default EmployeeNameMappingSyncDialog;
